import React, { Component } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Button, Typography } from "@material-ui/core";
import { Design, DesignField } from "services/entities/designs";
import ProgressSpinner from "components/ProgressSpinner";
import { loadDesigns } from "services/designs";
import { PromiseContainer } from "util/promises";
import RouteURLs from "RouteURLs";
import NextRouteURLs from "NextRouteURLs";
import { handleError } from "util/errors";
import "./index.scss";
import CreateDesignModal from "pages/designs/components/CreateDesignModal";
import NewResourceButton from "components/NewResourceButton";
import { CheckedItemIcon } from "components/Icon";
import { Space } from "services/entities/spaces";
import { formatPercentage } from "util/number";
import ExpandToggleButton from "pages/Dashboard/components/ExpandToggleButton";
import { ToggledContent } from "components/ToggledContent";

interface DesignsSectionState {
  creatingDesign: boolean;
  designs: Design[] | null;
  designsExpanded: boolean;
}

class DesignsSection extends Component<
  RouteComponentProps,
  DesignsSectionState
> {
  private promiseContainer: PromiseContainer | null = null;

  state: DesignsSectionState = {
    creatingDesign: false,
    designs: null,
    designsExpanded: true,
  };

  componentDidMount(): void {
    this.promiseContainer = new PromiseContainer();
    this.loadDesigns(this.promiseContainer);
  }

  componentWillUnmount(): void {
    if (this.promiseContainer) {
      this.promiseContainer.dispose();
      this.promiseContainer = null;
    }
  }

  loadDesigns(promiseContainer: PromiseContainer) {
    promiseContainer
      .add(loadDesigns({}, [DesignField.Space, DesignField.DesignScreenGroups]))
      .then(
        (designs) => this.setState({ designs: designs }),
        (ex) => {
          handleError("Failed to load designs", ex);
        }
      );
  }

  render() {
    const designs: Design[] = this.state.designs || [];
    const designsCount = designs.length;
    const designsLoaded = this.state.designs !== null;
    const hasDesigns = designs.length > 0;
    const expanded = this.state.designsExpanded;
    return (
      <section className="designs">
        <div className="header">
          <Typography variant="h2">
            My designs in progress
            {designsCount > 0 && (
              <span className="count">({designsCount})</span>
            )}
          </Typography>
          {designsLoaded && (
            <ExpandToggleButton
              onClick={this.onToggleDesignsExpandedClick}
              expanded={expanded}
            />
          )}
        </div>
        <ToggledContent
          visible={expanded}
          className={!designsLoaded ? "loading" : ""}
        >
          {!designsLoaded && <ProgressSpinner />}
          {designsLoaded && !hasDesigns && (
            <NewResourceButton
              className="new-design"
              onClick={this.onNewDesignClick}
              label="Create a new design"
              note="Brand your own space with images and videos"
            />
          )}
          {designsLoaded && hasDesigns && (
            <div className="items">
              {designs.map(this.renderDesign)}
              <div className="notice">
                <Typography variant="h5" component="h3">
                  Need assistance?
                </Typography>
                <Typography variant="body1">
                  We've created a checklist to help you prepare for your sook.
                </Typography>
                <Button
                  className="view"
                  onClick={this.onViewDesignChecklistClick}
                >
                  Contact Us
                </Button>
              </div>
            </div>
          )}
        </ToggledContent>
        <CreateDesignModal
          open={this.state.creatingDesign}
          onClosing={this.onNewDesignClosing}
          onCreated={this.onNewDesignCreated}
        />
      </section>
    );
  }

  renderDesign = (design: Design) => {
    const totalScreenCount = DesignsSection.getSpaceScreenGroupCount(
      design.space
    );
    const completedScreenCount =
      DesignsSection.getCompletedScreenGroupCount(design);
    const progressPercentage = formatPercentage(
      completedScreenCount / totalScreenCount,
      1
    );
    return (
      <div className="design item" key={design.code}>
        <Typography variant="h5" component="h3">
          {design.name}
        </Typography>
        <span className="space">{design.space.name}</span>
        <span className="progress-bar">
          <span className="progress" style={{ width: progressPercentage }} />
        </span>
        <span className="screens-progress">
          <CheckedItemIcon /> {completedScreenCount}/{totalScreenCount} Screens
          Ready
        </span>
        <Button size="small" className="view" onClick={this.onViewDesignClick}>
          View/edit design
        </Button>
      </div>
    );
  };

  onToggleDesignsExpandedClick = () => {
    this.setState({ designsExpanded: !this.state.designsExpanded });
  };

  onNewDesignClick = () => {
    this.setState({ creatingDesign: true });
  };

  onNewDesignClosing = () => {
    this.setState({ creatingDesign: false });
  };

  onNewDesignCreated = () => {
    this.setState({ creatingDesign: false });
    this.props.history.push(RouteURLs.DESIGN_LIST);
  };

  onViewDesignChecklistClick = () => {
    window.location.href = NextRouteURLs.CONTACT
  };

  onViewDesignClick = () => {
    this.props.history.push(RouteURLs.DESIGN_LIST);
  };

  static getSpaceScreenGroupCount(space: Space): number {
    let count = 0;
    for (const region of space.regions) {
      count += region.screenGroups.length;
    }
    return count;
  }

  static getCompletedScreenGroupCount(design: Design): number {
    let count = 0;
    for (const group of design.screenGroups) {
      if (group.ready) {
        count++;
      }
    }
    return count;
  }
}

export default withRouter(DesignsSection);
